import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useTickets } from 'contexts/TicketsContext';
import { RoutePathsE } from 'declarations/enums/routes';
import { ActiveTabTypeE } from 'declarations/enums/tab';
import { TicketsRefinementKeysE } from 'declarations/enums/tickets';
import type { ScheduleFileI } from 'declarations/interfaces/scheduleFiles';
import type { NoopT } from 'declarations/types/common';
import { useFetchScheduleFiles } from 'hooks/api/useTickets';

type UseViewScheduleFilesPopupT = {
  scheduleFiles: ScheduleFileI[] | null;
  isLoadingScheduleFiles: boolean;
  isScheduleFilesFetchingError: boolean;
  isDeleteScheduleFilePopupOpened: boolean;
  handleCloseDeleteScheduleFilePopup: NoopT;
  selectedScheduleFile: ScheduleFileI | null;
  handleOpenDeleteScheduleFilePopup: (schdeuleFile: ScheduleFileI) => NoopT;
  handleOpenScheduleFilePreview: (scheduleFile: ScheduleFileI) => void;
  handleOpenScheduleTicketsView: (scheduleFile: ScheduleFileI) => void;
};

type UseViewScheduleFilesPopupParamsT = {
  rooftopId: string;
  isPopupOpened: boolean;
};

const useViewScheduleFilesPopup = ({
  rooftopId,
  isPopupOpened,
}: UseViewScheduleFilesPopupParamsT): UseViewScheduleFilesPopupT => {
  /**
   * Hooks
   */
  const {
    setActiveTab,
    setIsScheduleTicketList,
    setSelectedSchedule,
    setViewScheduleFilesPopupVisibility,
  } = useTickets();
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedScheduleFile, selectScheduleFile] = useState<null | ScheduleFileI>(null);
  const [isDeleteScheduleFilePopupOpened, setDeleteScheduleFilePopupVisibility] = useState(false);

  /**
   * Variables
   */
  const shouldFetchScheduleFiles = !!rooftopId && isPopupOpened;

  /**
   * Api hooks
   */
  const scheduleFilesResult = useFetchScheduleFiles({
    rooftopId,
    shouldFetch: shouldFetchScheduleFiles,
  });

  const handleOpenScheduleFilePreview: (scheduleFile: ScheduleFileI) => void = (
    scheduleFile: ScheduleFileI
  ): void => {
    if (scheduleFile.fileUrl) {
      window.open(scheduleFile.fileUrl, '_blank', 'noopener ,noreferrer');
    }
  };

  /**
   * Handlers
   */
  const handleOpenDeleteScheduleFilePopup: (scheduleFile: ScheduleFileI) => () => void =
    (scheduleFile: ScheduleFileI) => (): void => {
      setDeleteScheduleFilePopupVisibility(true);
      selectScheduleFile(scheduleFile);
    };
  const handleCloseDeleteScheduleFilePopup: () => void = (): void => {
    setDeleteScheduleFilePopupVisibility(false);
    selectScheduleFile(null);
  };
  const handleOpenScheduleTicketsView: (scheduleFile: ScheduleFileI) => void = (
    scheduleFile: ScheduleFileI
  ): void => {
    setActiveTab(ActiveTabTypeE.Schedule);
    sessionStorage.setItem('activeTab', ActiveTabTypeE.Schedule);
    setIsScheduleTicketList(true);
    setSelectedSchedule(scheduleFile);
    setViewScheduleFilesPopupVisibility(false);
    const params = new URLSearchParams(location.search);
    params.set(TicketsRefinementKeysE.ScheduleFileId, String(scheduleFile.id));
    const search = params.toString() ? `?${params.toString()}` : '';
    navigate(`${RoutePathsE.TICKETS_PAGE}${search}`);
  };

  return {
    scheduleFiles: scheduleFilesResult.data ?? null,
    isLoadingScheduleFiles: scheduleFilesResult.isLoading || scheduleFilesResult.isRefetching,
    isScheduleFilesFetchingError: scheduleFilesResult.isError,
    isDeleteScheduleFilePopupOpened,
    handleOpenDeleteScheduleFilePopup,
    handleCloseDeleteScheduleFilePopup,
    selectedScheduleFile,
    handleOpenScheduleFilePreview,
    handleOpenScheduleTicketsView,
  };
};

export default useViewScheduleFilesPopup;
